import { Accordion, Button, spacingHelper, typographyHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { createSearchUrl } from '../../../helpers/createSearchUrl';
import { getPropertyStatusFromUrl } from '../../../helpers/queryStringHelpers/queryStringHelpers';
import { SearchModal } from '../../SearchModal/SearchModal';
import { formatCommuteTimeForDisplay } from './helpers/formatCommuteTimeForDisplay';
import { getCommuteFiltersFromUrl } from './helpers/getCommuteFiltersFromUrl';
import { getInitialCommute } from './helpers/getInitialCommute';
import { mapCommuteMethodToIcon } from './helpers/mapCommuteMethodToIcon';
import { CommuteMethodRadioInput } from './subcomponents/CommuteMethodRadioInput/CommuteMethodRadioInput';
import { CommuteTimeInput } from './subcomponents/CommuteTimeInput/CommuteTimeInput';
import { FilterAccordionItem } from './subcomponents/FilterAccordionItem/FilterAccordionItem';
import { PointOfInterestSuggest } from './subcomponents/PointOfInterestSuggest/PointOfInterestSuggest';
import { CommuteMethods, type Commute } from './types';
import { SearchPropertyStatus } from '../../../types';

const LocationSearchButton = styled(Button)`
  ${typographyHelper('body01')}
`;

const CommuteMethodsGroup = styled.div`
  margin: ${spacingHelper('medium')} 0;
  display: grid;
  gap: ${spacingHelper('small')};
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr;
`;

type LocationSearchButton = {
  label: string;
  onClick: () => void;
};

interface CommuteAndLocationModalCommuteScreenContentProps {
  locationSearchButton: LocationSearchButton;
  pointOfInterestSuggest: {
    label: string;
    placeholder: string;
    noResultsMessage: string;
    id?: string;
  };
  commute: Commute;
  updateCommute: (newCommute: Commute) => void;
}

export const CommuteAndLocationModalCommuteScreenContent: FunctionComponent<
  CommuteAndLocationModalCommuteScreenContentProps
> = ({ locationSearchButton, pointOfInterestSuggest, commute, updateCommute }) => (
  <>
    <Accordion>
      <FilterAccordionItem
        title="Destination"
        badgeIcon={mapCommuteMethodToIcon(commute.method)}
        badgeText={formatCommuteTimeForDisplay(commute.time)}
        icon={null}
        expanded
      >
        <PointOfInterestSuggest
          {...pointOfInterestSuggest}
          selectedOption={
            commute?.destination
              ? {
                  value: commute.destination.placeId,
                  suggestionText: commute.destination.label,
                  selectedText: commute.destination.label,
                }
              : null
          }
          onSelectedOptionChange={(option) => {
            const newDestination = option
              ? { placeId: option.value, label: option.selectedText }
              : null;
            updateCommute({ ...commute, destination: newDestination });
          }}
        />
        <CommuteMethodsGroup>
          {CommuteMethods.map((commuteMethod) => (
            <CommuteMethodRadioInput
              key={commuteMethod}
              method={commuteMethod}
              isSelected={commuteMethod === commute.method}
              onChange={(method) => updateCommute({ ...commute, method })}
            />
          ))}
        </CommuteMethodsGroup>
        <CommuteTimeInput
          commuteTime={commute.time}
          onChange={(time) => updateCommute({ ...commute, time })}
        />
      </FilterAccordionItem>
    </Accordion>
    <LocationSearchButton variant="link-primary" onClick={locationSearchButton.onClick}>
      {locationSearchButton.label}
    </LocationSearchButton>
  </>
);

interface CommuteAndLocationModalCommuteScreenProps {
  onClose: () => void;
  title: string;
  subtitle: string;
  locationSearchButton: {
    label: string;
    onClick: () => void;
  };
  pointOfInterestSuggest: {
    label: string;
    placeholder: string;
    noResultsMessage: string;
    id?: string;
    currentSearchPlaceName?: string | null;
  };
  searchQuery: string;
}

const createSearchCommuteQueryParamFromCommute = ({ destination, method, time }: Commute) =>
  `${destination?.placeId}|${method}|${time}`;

export const replaceForRentPropertyStatusWithAllPropertyStatus = (
  searchQuery: string,
): SearchPropertyStatus => {
  // Temporarily remove ForRent status from query string in commute, replace with All.
  const propertyStatusFromUrl = getPropertyStatusFromUrl(searchQuery);
  return propertyStatusFromUrl === SearchPropertyStatus.forRent
    ? SearchPropertyStatus.all
    : propertyStatusFromUrl;
};

export const CommuteAndLocationModalCommuteScreen: FunctionComponent<
  CommuteAndLocationModalCommuteScreenProps
> = ({ title, subtitle, onClose, locationSearchButton, pointOfInterestSuggest, searchQuery }) => {
  const { currentSearchPlaceName } = pointOfInterestSuggest;

  const commuteFilters = getCommuteFiltersFromUrl(searchQuery);

  const initialCommute = getInitialCommute(commuteFilters, currentSearchPlaceName);

  const [commute, setCommute] = useState<Commute>(initialCommute);

  const onConfirm = () => {
    createSearchUrl({
      currentUrl: searchQuery,
      updates: {
        flow: 'commute',
        commutes: createSearchCommuteQueryParamFromCommute(commute),
        locations: undefined,
        boundingBox: undefined,
        surroundingSuburbs: undefined,
        excludeUnderOffer: undefined,
        excludeDepositTaken: undefined,
        propertyStatus: replaceForRentPropertyStatusWithAllPropertyStatus(searchQuery),
        pageNumber: '',
      },
      historyPushFn: (url) => window.location.assign(url),
    });

    onClose();
  };

  const noCommuteDestinationSelected = commute.destination === null;

  return (
    <SearchModal
      opened
      title={title}
      subtitle={subtitle}
      onCancel={onClose}
      onConfirm={onConfirm}
      disableConfirm={noCommuteDestinationSelected}
    >
      <CommuteAndLocationModalCommuteScreenContent
        locationSearchButton={locationSearchButton}
        pointOfInterestSuggest={pointOfInterestSuggest}
        commute={commute}
        updateCommute={(newCommute: Commute) => setCommute(newCommute)}
      />
    </SearchModal>
  );
};
